import React, { useState } from "react";
import {
  Box, Typography, Card, CardContent, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, IconButton, Tooltip, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider
} from "@mui/material";
import { motion } from "framer-motion";
import Swal from "sweetalert2";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";

const brand = "#800b33";
const brandHover = "#6c0a2b";

const WaiverApprovalDashboard = ({ role = "Chairperson" }) => {
  const [requests, setRequests] = useState([
    {
      id: 1,
      member: "Asha Juma",
      reason: "Sick for 3 days — could not repay on time",
      start: "2025-10-23",
      end: "2025-10-25",
      status: "Pending",
      submittedOn: "2025-10-25",
    },
    {
      id: 2,
      member: "John Paulo",
      reason: "Lost phone access temporarily",
      start: "2025-10-21",
      end: "2025-10-23",
      status: "Approved",
      approvedBy: "Katibu",
      approvedOn: "2025-10-26",
    },
  ]);

  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);

  const handleView = (req) => { setSelected(req); setOpen(true); };
  const handleClose = () => setOpen(false);

  const handleApprove = (req) => {
    Swal.fire({
      title: "Approve Waiver?",
      text: `Approve ${req.member}'s penalty waiver?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: brand,
      confirmButtonText: "Approve",
    }).then((res) => {
      if (res.isConfirmed) {
        setRequests((prev) =>
          prev.map((r) =>
            r.id === req.id
              ? {
                  ...r,
                  status: "Approved",
                  approvedBy: role,
                  approvedOn: dayjs().format("YYYY-MM-DD HH:mm"),
                }
              : r
          )
        );
        Swal.fire({
          icon: "success",
          title: "Waiver Approved",
          text: `${req.member}'s waiver has been approved.`,
          confirmButtonColor: brand,
        });
      }
    });
  };

  const handleReject = (req) => {
    Swal.fire({
      title: "Reject Waiver?",
      input: "text",
      inputPlaceholder: "Enter rejection reason",
      inputValidator: (v) => (!v ? "Reason is required!" : undefined),
      showCancelButton: true,
      confirmButtonColor: "#d32f2f",
    }).then((res) => {
      if (res.isConfirmed) {
        setRequests((prev) =>
          prev.map((r) =>
            r.id === req.id
              ? {
                  ...r,
                  status: "Rejected",
                  rejectedBy: role,
                  rejectedOn: dayjs().format("YYYY-MM-DD HH:mm"),
                  rejectionReason: res.value,
                }
              : r
          )
        );
        Swal.fire({
          icon: "info",
          title: "Waiver Rejected",
          text: `${req.member}'s request was rejected. Reason: ${res.value}`,
        });
      }
    });
  };

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: (i = 1) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.1, duration: 0.4 },
    }),
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Typography variant="h5" sx={{ fontWeight: 900, color: brand, mb: 1 }}>
        Penalty Waiver Approvals — {role}
      </Typography>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 3 }}>
        Review, approve, or reject penalty waiver requests from members.
      </Typography>

      <motion.div variants={fadeIn} initial="hidden" animate="visible">
        <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
          <CardContent sx={{ p: 0 }}>
            <TableContainer component={Paper} elevation={0}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Member</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Period</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Submitted</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {requests.map((r, i) => (
                    <TableRow key={r.id} hover>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>{r.member}</TableCell>
                      <TableCell>{r.reason}</TableCell>
                      <TableCell>{r.start} → {r.end}</TableCell>
                      <TableCell>
                        <Chip
                          label={r.status}
                          color={
                            r.status === "Approved"
                              ? "success"
                              : r.status === "Rejected"
                              ? "error"
                              : "warning"
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{r.submittedOn}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="View Details">
                          <IconButton onClick={() => handleView(r)}>
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>

                        {r.status === "Pending" && (
                          <>
                            <Tooltip title="Approve">
                              <IconButton
                                color="success"
                                onClick={() => handleApprove(r)}
                              >
                                <CheckCircleIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject">
                              <IconButton
                                color="error"
                                onClick={() => handleReject(r)}
                              >
                                <CancelIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </motion.div>

      {/* View Details Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
          Waiver Request Details
          <IconButton onClick={() => setOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Box sx={{ display: "grid", gap: 1.5 }}>
              <Typography><strong>Member:</strong> {selected.member}</Typography>
              <Typography><strong>Reason:</strong> {selected.reason}</Typography>
              <Typography><strong>Period:</strong> {selected.start} → {selected.end}</Typography>
              <Typography><strong>Status:</strong> {selected.status}</Typography>

              {selected.approvedBy && (
                <Typography color="success.main">
                  ✅ Approved by {selected.approvedBy} on {selected.approvedOn}
                </Typography>
              )}
              {selected.rejectedBy && (
                <Typography color="error.main">
                  ❌ Rejected by {selected.rejectedBy} — {selected.rejectionReason}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WaiverApprovalDashboard;
